import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { useUserDocumentData } from '../hooks/useUserDocument';
import { useAccountType } from './AccountTypeContext';
import { useAuth } from './AuthContext';
import { backendAIService } from '../services/backendAIService';
import { AITradingData, AIStrategy } from '../types/userDocument';
import { positionEventEmitter } from '../services/positionEventEmitter';

interface BackendAITradingContextType {
  // Service status
  isServiceRunning: boolean;
  serviceAvailable: boolean;
  activeStrategiesCount: number;
  
  // AI Trading data
  aiTradingData: AITradingData | null;
  isAIEnabled: boolean; // Global AI enabled state
  strategies: AIStrategy[]; // All available strategies (backward compatibility)
  activeStrategies: any[]; // Active strategies (backward compatibility)
  
  // Per-asset AI trading functionality
  getAssetAIConfig: (symbol: string) => any | null;
  setAssetAIEnabled: (symbol: string, enabled: boolean) => Promise<void>;
  stopAssetAITrading: (symbol: string) => Promise<void>; // NEW: Comprehensive stop method
  removeStrategyCompletely: (symbol: string) => Promise<void>; // NEW: Complete removal method
  isAssetAIEnabled: (symbol: string) => boolean;
  saveAssetStrategy: (symbol: string, strategy: any) => Promise<void>; // NEW: Save strategy (doesn't start AI)
  startAssetAITrading: (symbol: string) => Promise<void>; // NEW: Start AI trading
  stopAssetAITradingNew: (symbol: string) => Promise<void>; // NEW: Stop AI trading
  getAssetStrategies: (symbol: string) => any[];
  getActiveAssets: () => string[];
  getAllActiveAssets: () => Array<{ symbol: string; config: any }>;
  
  // Data refresh functionality
  loadAITradingData: () => Promise<void>; // NEW: Force reload AI data from Firestore
  
  // Migration and maintenance
  fixAssetEnabledStates: () => Promise<void>;
  
  // Global actions (backward compatibility)
  enableAI: () => Promise<void>;
  disableAI: () => Promise<void>;
  addStrategy: (strategy: Omit<AIStrategy, 'id' | 'createdAt' | 'lastModified'>) => Promise<void>;
  updateStrategy: (strategyId: string, updates: Partial<AIStrategy>) => Promise<void>;
  removeStrategy: (strategyId: string) => Promise<void>;
  activateStrategy: (strategyId: string) => Promise<void>;
  deactivateStrategy: (strategyId: string) => Promise<void>;
  
  // Service control
  startBackendService: () => Promise<void>;
  stopBackendService: () => Promise<void>;
  
  // Status
  isLoading: boolean;
  error: string | null;
}

const BackendAITradingContext = createContext<BackendAITradingContextType | undefined>(undefined);

export const useBackendAITrading = () => {
  const context = useContext(BackendAITradingContext);
  if (context === undefined) {
    throw new Error('useBackendAITrading must be used within a BackendAITradingProvider');
  }
  return context;
};

interface BackendAITradingProviderProps {
  children: ReactNode;
}

export const BackendAITradingProvider: React.FC<BackendAITradingProviderProps> = ({ children }) => {
  const { accountType } = useAccountType();
  const { updateAITradingData, isLoading, error, demoAccountData, realAccountData } = useUserDocumentData();
  const { user } = useAuth();
  
  // Service status
  const [isServiceRunning, setIsServiceRunning] = useState(false);
  const [serviceAvailable, setServiceAvailable] = useState(false);
  const [activeStrategiesCount, setActiveStrategiesCount] = useState(0);
  const [serviceCheckAttempted, setServiceCheckAttempted] = useState(false);
  
  // AI Trading data
  const [aiTradingData, setAiTradingData] = useState<AITradingData | null>(null);
  const [isAIEnabled, setIsAIEnabled] = useState(false);
  const [strategies, setStrategies] = useState<AIStrategy[]>([]);
  const [activeStrategies, setActiveStrategies] = useState<any[]>([]);

  /**
   * Check service availability and status
   */
  const checkServiceStatus = useCallback(async () => {
    // If we've already determined the service is unavailable, don't check again
    if (serviceCheckAttempted && !serviceAvailable) {
      return;
    }
    
    try {
      setServiceCheckAttempted(true);
      const available = await backendAIService.isServiceAvailable();
      setServiceAvailable(available);
      
      if (available) {
        const status = await backendAIService.getStatus();
        setIsServiceRunning(status.isRunning);
        setActiveStrategiesCount(status.activeStrategies);
      }
    } catch (error) {
      // Silently handle service unavailable - don't log errors
      setServiceAvailable(false);
      setServiceCheckAttempted(true);
    }
  }, [serviceCheckAttempted, serviceAvailable]);

  /**
   * Restore and auto-start active AI strategies from Firestore
   * Only auto-starts strategies that were previously running (both aiTradeStartEnabled AND isActive are true)
   */
  const restoreActiveStrategies = useCallback(async (aiData: AITradingData) => {
    try {
     
      // Handle both cases: activeStrategies as objects or as IDs
      const activeStrategies = (aiData.activeStrategies as any[]) || [];
      const allStrategies = (aiData.strategies as any[]) || [];
      
      if (!Array.isArray(activeStrategies) || activeStrategies.length === 0) {
        return;
      }

      // Process each active strategy
      for (const activeStrategyData of activeStrategies) {
        
        let strategy: any = null;
        
        // Handle two cases: full object or just an ID
        if (typeof activeStrategyData === 'string') {
          // activeStrategies contains IDs, look up full object from strategies array
          strategy = allStrategies.find((s: any) => s.id === activeStrategyData);
          
          if (!strategy) {
            
            // Try loading directly from Firestore as fallback
            try {
              if (user) {
                const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
                // We need a symbol to load - this is a limitation, let's log for now
              }
            } catch (error) {
              console.warn('Failed to load strategy from Firestore:', error);
            }
            continue;
          }
        } else if (typeof activeStrategyData === 'object') {
          // activeStrategies contains full objects
          strategy = activeStrategyData;
        } else {

        }
             
        // Check if strategy meets auto-start criteria
        // CRITICAL: Only auto-start if BOTH aiTradeStartEnabled AND isActive are true
        // This prevents newly saved strategies from auto-starting
        if (strategy.aiTradeStartEnabled === true && 
            strategy.isActive === true && 
            (strategy.enabled === true || strategy.enabled === undefined)) { // enabled might be undefined in some cases
          
          // console.log(`🚀 Auto-starting strategy: ${strategy.name} (${strategy.symbol}) - aiTradeStartEnabled: ${strategy.aiTradeStartEnabled}, isActive: ${strategy.isActive}`);
          
          try {
            // Mark AI as enabled since we have active strategies
            setIsAIEnabled(true);
            
            // Also start the strategy in the AI trading context for the specific symbol
            if (strategy.symbol) {
              // Import the AI trading context to start the strategy
              try {
                // We'll trigger a custom event that the AITradingContext can listen to
                const event = new CustomEvent('autoStartAIStrategy', {
                  detail: {
                    symbol: strategy.symbol,
                    strategy: strategy.type, // 'supertrend', etc.
                    config: strategy.config
                  }
                });
                window.dispatchEvent(event);
                // console.log(`📡 Dispatched auto-start event for ${strategy.name} (${strategy.symbol})`);
              } catch (eventError) {
                console.warn('Failed to dispatch auto-start event:', eventError);
              }
            }
                       
          } catch (strategyError) {
            console.error(`❌ Failed to configure AI strategy ${strategy.name}:`, strategyError);
          }
        } else {
          // console.log(`⏸️ Skipping auto-start for strategy: ${strategy.name} (${strategy.symbol}) - aiTradeStartEnabled: ${strategy.aiTradeStartEnabled}, isActive: ${strategy.isActive}`);
        }
      }
      
    } catch (error) {
      console.error('❌ Error restoring active strategies:', error);
    }
  }, [user]);

  /**
   * Load AI trading data from user document
   */
  const loadAITradingData = useCallback(async () => {
    // Reduce logging to prevent infinite loops
    try {
      // 🔥 CRITICAL FIX: Check authentication and prevent infinite loops
      if (!user?.uid) {
        // Only log once to prevent spam
        if (!aiTradingData) {
          // console.log(`❌ No user ID available for ${accountType} - setting default data`);
        }
        // Set default empty data to prevent infinite loops
        const defaultAiData: AITradingData = {
          globalEnabled: false,
          globalSettings: {
            maxConcurrentAssets: 5,
            globalRiskLimit: 10,
            globalDailyLoss: 20,
            emergencyStopEnabled: true
          },
          assets: {},
          globalPerformance: {
            totalTrades: 0,
            winningTrades: 0,
            losingTrades: 0,
            winRate: 0,
            totalPnL: 0,
            averageTradeDuration: 0,
            sharpeRatio: 0,
            maxDrawdown: 0,
            activeAssets: []
          },
          lastUpdated: new Date(),
          enabled: false,
          strategies: [],
          activeStrategies: []
        };
        
        setAiTradingData(defaultAiData);
        setIsAIEnabled(false);
        setStrategies([]);
        setActiveStrategies([]);
        return;
      }

      // Import the user document service to load data directly from Firestore
      const { UserDocumentService } = await import('../services/userDocumentService');
      
      // Load the user document directly from Firestore
      const userDocument = await UserDocumentService.getUserDocument(user.uid);
      const accountData = userDocument?.accounts?.[accountType === 'live' ? 'real' : 'demo'];
      
      // console.log(`🔍 loadAITradingData for ${accountType} (direct from Firestore):`, {
      //   hasUserDocument: !!userDocument,
      //   hasAccountData: !!accountData,
      //   hasAiTradingData: !!accountData?.aiTradingData,
      //   hasAssets: !!accountData?.aiTradingData?.assets,
      //   assetsKeys: accountData?.aiTradingData?.assets ? Object.keys(accountData.aiTradingData.assets) : [],
      //   fullAiTradingData: accountData?.aiTradingData
      // });
      
      
      // Use actual data from Firebase or create default with new structure
      const aiData: AITradingData = accountData?.aiTradingData || {
        globalEnabled: false,
        globalSettings: {
          maxConcurrentAssets: 5,
          globalRiskLimit: 10,
          globalDailyLoss: 20,
          emergencyStopEnabled: true
        },
        assets: {},
        globalPerformance: {
          totalTrades: 0,
          winningTrades: 0,
          losingTrades: 0,
          winRate: 0,
          totalPnL: 0,
          averageTradeDuration: 0,
          sharpeRatio: 0,
          maxDrawdown: 0,
          activeAssets: []
        },
        lastUpdated: new Date(),
        // Backward compatibility fields
        enabled: false,
        strategies: [],
        activeStrategies: [],
        performance: {
          totalTrades: 0,
          winningTrades: 0,
          losingTrades: 0,
          winRate: 0,
          totalPnL: 0,
          averageTradeDuration: 0,
          sharpeRatio: 0,
          maxDrawdown: 0
        },
        settings: {
          maxPositions: 5,
          maxRiskPerTrade: 2,
          maxDailyLoss: 10,
          autoStopLoss: true,
          autoTakeProfit: true,
          riskManagementEnabled: true
        }
      };

      // Also try to load active strategies directly from Firestore for more accurate data
      if (user) {
        try {
          // Let's also inspect the raw Firestore data to understand the structure
         
          const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
          
          const firestoreStrategies = await AIStrategyPersistenceService.loadAllActiveStrategies(
            user.uid,
            accountType === 'live' ? 'live' : 'demo'
          );
          
         
          if (firestoreStrategies.length > 0) {
            // console.log(`🔥 BackendAITrading: Found ${firestoreStrategies.length} strategies from Firestore:`, firestoreStrategies.map(s => ({ symbol: s.symbol, id: s.id, name: s.name })));
            
            // Update aiData with fresh Firestore data
            // Update backward compatibility fields
            if (aiData.strategies) {
              aiData.strategies = firestoreStrategies as any[];
            }
            if (aiData.activeStrategies) {
              aiData.activeStrategies = firestoreStrategies as any;
            }
            if (aiData.enabled !== undefined) {
              aiData.enabled = true; // Enable if there are active strategies
            }
            
            // 🔥 CRITICAL FIX: Map activeStrategies to assets structure
            if (!aiData.assets) {
              aiData.assets = {};
              // console.log(`🔥 BackendAITrading: Created empty assets object`);
            }
            
            // Map each strategy to the assets structure
            firestoreStrategies.forEach((strategy: any) => {
              if (strategy.symbol) {
                // console.log(`🔥 BackendAITrading: Mapping strategy ${strategy.symbol} to assets structure:`, {
                //   symbol: strategy.symbol,
                //   id: strategy.id,
                //   name: strategy.name,
                //   isActive: strategy.isActive,
                //   aiTradeStartEnabled: strategy.aiTradeStartEnabled,
                //   hasConfig: !!strategy.config
                // });
                
                aiData.assets[strategy.symbol] = {
                  enabled: strategy.isActive || strategy.aiTradeStartEnabled || false,
                  selectedStrategy: strategy.id || strategy.name,
                  activeStrategyInstance: strategy,
                  availableStrategies: [strategy],
                  performance: {
                    totalTrades: 0,
                    winningTrades: 0,
                    losingTrades: 0,
                    winRate: 0,
                    totalPnL: 0,
                    averageTradeDuration: 0,
                    sharpeRatio: 0,
                    maxDrawdown: 0
                  },
                  settings: {
                    maxPositions: 1,
                    maxRiskPerTrade: 2,
                    maxDailyLoss: 10,
                    autoStopLoss: true,
                    autoTakeProfit: true,
                    riskManagementEnabled: true
                  },
                  lastUpdated: new Date()
                };
              }
            });
            
            // console.log(`🔥 BackendAITrading: Final assets structure:`, {
            //   assetsKeys: Object.keys(aiData.assets),
            //   ethusdtExists: !!aiData.assets.ETHUSDT,
            //   ethusdtData: aiData.assets.ETHUSDT
            // });
            
            // Update global enabled state
            aiData.globalEnabled = true;
            
          } else {
            // If AIStrategyPersistenceService returns empty, let's examine what's actually in the raw data
            
            if (accountData && accountData.aiTradingData) {
              const rawAiData = accountData.aiTradingData;
              
              // Check if activeStrategies contains full objects
              if (rawAiData.activeStrategies && Array.isArray(rawAiData.activeStrategies)) {
                if (rawAiData.activeStrategies.length > 0) {
                  const firstStrategy = rawAiData.activeStrategies[0];
                  
                  // If the raw activeStrategies contains full objects, use them directly
                  if (typeof firstStrategy === 'object' && firstStrategy !== null && (firstStrategy as any).id) {
                    // console.log(`🔥 BackendAITrading: Using raw activeStrategies data:`, rawAiData.activeStrategies.map((s: any) => ({ symbol: s.symbol, id: s.id, name: s.name })));
                    
                    aiData.strategies = rawAiData.activeStrategies as any[]; // Cast to resolve type issues
                    aiData.activeStrategies = rawAiData.activeStrategies as any; // All strategies for component access
                    aiData.enabled = true;
                    
                    // 🔥 CRITICAL FIX: Map activeStrategies to assets structure for raw data too
                    if (!aiData.assets) {
                      aiData.assets = {};
                      // console.log(`🔥 BackendAITrading: Created empty assets object for raw data`);
                    }
                    
                    // Map each strategy to the assets structure
                    rawAiData.activeStrategies.forEach((strategy: any) => {
                      if (strategy.symbol) {
                        // console.log(`🔥 BackendAITrading: Mapping raw strategy ${strategy.symbol} to assets structure:`, {
                        //   symbol: strategy.symbol,
                        //   id: strategy.id,
                        //   name: strategy.name,
                        //   isActive: strategy.isActive,
                        //   aiTradeStartEnabled: strategy.aiTradeStartEnabled,
                        //   hasConfig: !!strategy.config
                        // });
                        
                        aiData.assets[strategy.symbol] = {
                          enabled: strategy.isActive || strategy.aiTradeStartEnabled || false,
                          selectedStrategy: strategy.id || strategy.name,
                          activeStrategyInstance: strategy,
                          availableStrategies: [strategy],
                          performance: {
                            totalTrades: 0,
                            winningTrades: 0,
                            losingTrades: 0,
                            winRate: 0,
                            totalPnL: 0,
                            averageTradeDuration: 0,
                            sharpeRatio: 0,
                            maxDrawdown: 0
                          },
                          settings: {
                            maxPositions: 1,
                            maxRiskPerTrade: 2,
                            maxDailyLoss: 10,
                            autoStopLoss: true,
                            autoTakeProfit: true,
                            riskManagementEnabled: true
                          },
                          lastUpdated: new Date()
                        };
                      }
                    });
                    
                    // console.log(`🔥 BackendAITrading: Final raw assets structure:`, {
                    //   assetsKeys: Object.keys(aiData.assets),
                    //   ethusdtExists: !!aiData.assets.ETHUSDT,
                    //   ethusdtData: aiData.assets.ETHUSDT
                    // });
                  }
                }
              }
            }
          }
        } catch (firestoreError) {
          console.warn('Could not load fresh strategies from Firestore:', firestoreError);
        }
      }

      // Update local state
      // console.log(`🔄 Setting aiTradingData for ${accountType}:`, {
      //   hasAssets: !!aiData.assets,
      //   assetsKeys: aiData.assets ? Object.keys(aiData.assets) : [],
      //   ethusdtExists: !!aiData.assets?.ETHUSDT,
      //   activeStrategiesLength: aiData.activeStrategies?.length || 0,
      //   fullAiData: aiData
      // });
      
      setAiTradingData(aiData);
      setIsAIEnabled(aiData.globalEnabled || aiData.enabled || false);
      setStrategies(aiData.strategies || []);
      setActiveStrategies(aiData.activeStrategies || []);
      
      // 🔄 Auto-restore active AI strategies on app load/refresh
      await restoreActiveStrategies(aiData);
      
    } catch (error) {
      console.error('Error loading AI trading data:', error);
    }
  }, [accountType, demoAccountData, realAccountData, user, restoreActiveStrategies]);

  /**
   * Enable AI trading
   */
  const enableAI = useCallback(async () => {
    try {
      const updates: Partial<AITradingData> = {
        enabled: true,
        lastUpdated: new Date()
      };
      
      await updateAITradingData(accountType, updates);
      setIsAIEnabled(true);
      
      // Start backend service if not running
      if (!isServiceRunning && serviceAvailable) {
        await startBackendService();
      }
      
    } catch (error) {
      console.error('Error enabling AI:', error);
      throw error;
    }
  }, [accountType, updateAITradingData, isServiceRunning, serviceAvailable]);

  /**
   * Disable AI trading
   */
  const disableAI = useCallback(async () => {
    try {
      const updates: Partial<AITradingData> = {
        enabled: false,
        lastUpdated: new Date()
      };
      
      await updateAITradingData(accountType, updates);
      setIsAIEnabled(false);
      
      // Stop backend service
      if (isServiceRunning && serviceAvailable) {
        await stopBackendService();
      }
      
    } catch (error) {
      console.error('Error disabling AI:', error);
      throw error;
    }
  }, [accountType, updateAITradingData, isServiceRunning, serviceAvailable]);

  /**
   * Add a new strategy
   */
  const addStrategy = useCallback(async (strategyData: Omit<AIStrategy, 'id' | 'createdAt' | 'lastModified'>) => {
    try {
      const newStrategy: AIStrategy = {
        ...strategyData,
        id: `strategy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date(),
        lastModified: new Date()
      };

      const updatedStrategies = [...strategies, newStrategy];
      const updates: Partial<AITradingData> = {
        strategies: updatedStrategies,
        lastUpdated: new Date()
      };

      await updateAITradingData(accountType, updates);
      setStrategies(updatedStrategies);
    } catch (error) {
      console.error('Error adding strategy:', error);
      throw error;
    }
  }, [accountType, updateAITradingData, strategies]);

  /**
   * Update a strategy
   */
  const updateStrategy = useCallback(async (strategyId: string, updates: Partial<AIStrategy>) => {
    try {
      const updatedStrategies = strategies.map(strategy => 
        strategy.id === strategyId 
          ? { ...strategy, ...updates, lastModified: new Date() }
          : strategy
      );

      const aiUpdates: Partial<AITradingData> = {
        strategies: updatedStrategies,
        lastUpdated: new Date()
      };

      await updateAITradingData(accountType, aiUpdates);
      setStrategies(updatedStrategies);
    } catch (error) {
      console.error('Error updating strategy:', error);
      throw error;
    }
  }, [accountType, updateAITradingData, strategies]);

  /**
   * Remove a strategy
   */
  const removeStrategy = useCallback(async (strategyId: string) => {
    try {
      const updatedStrategies = strategies.filter(strategy => strategy.id !== strategyId);
      const updatedActiveStrategies = activeStrategies.filter((strategy: any) => strategy?.id !== strategyId);

      const updates: Partial<AITradingData> = {
        strategies: updatedStrategies,
        activeStrategies: updatedActiveStrategies,
        lastUpdated: new Date()
      };

      await updateAITradingData(accountType, updates);
      setStrategies(updatedStrategies);
      setActiveStrategies(updatedActiveStrategies);
    } catch (error) {
      console.error('Error removing strategy:', error);
      throw error;
    }
  }, [accountType, updateAITradingData, strategies, activeStrategies]);

  /**
   * Activate a strategy
   */
  const activateStrategy = useCallback(async (strategyId: string) => {
    try {
      if (!activeStrategies.includes(strategyId)) {
        const updatedActiveStrategies = [...activeStrategies, strategyId];
        
        // Find the strategy to get its name
        const strategy = strategies.find(s => s.id === strategyId);
        const strategyName = strategy?.name || 'Unknown Strategy';
        
        const updates: Partial<AITradingData> = {
          activeStrategies: updatedActiveStrategies,
          activeStrategyName: strategyName, // Store the active strategy name
          lastUpdated: new Date()
        };

        await updateAITradingData(accountType, updates);
        setActiveStrategies(updatedActiveStrategies);
        
      }
    } catch (error) {
      console.error('Error activating strategy:', error);
      throw error;
    }
  }, [accountType, updateAITradingData, activeStrategies, strategies]);

  /**
   * Deactivate a strategy
   */
  const deactivateStrategy = useCallback(async (strategyId: string) => {
    try {
      const updatedActiveStrategies = activeStrategies.filter((strategy: any) => strategy?.id !== strategyId);
      
      const updates: Partial<AITradingData> = {
        activeStrategies: updatedActiveStrategies,
        lastUpdated: new Date()
      };
      
      // Only clear activeStrategyName if there are no active strategies
      if (updatedActiveStrategies.length === 0) {
        updates.activeStrategyName = undefined;
      }

      await updateAITradingData(accountType, updates);
      setActiveStrategies(updatedActiveStrategies);
      
    } catch (error) {
      console.error('Error deactivating strategy:', error);
      throw error;
    }
  }, [accountType, updateAITradingData, activeStrategies]);

  /**
   * Start backend service
   */
  const startBackendService = useCallback(async () => {
    try {
      const response = await backendAIService.startService();
      if (response.status === 'success') {
        setIsServiceRunning(true);
        await checkServiceStatus();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error starting backend service:', error);
      throw error;
    }
  }, [checkServiceStatus]);

  /**
   * Stop backend service
   */
  const stopBackendService = useCallback(async () => {
    try {
      const response = await backendAIService.stopService();
      if (response.status === 'success') {
        setIsServiceRunning(false);
        await checkServiceStatus();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error stopping backend service:', error);
      throw error;
    }
  }, [checkServiceStatus]);

  // Initialize on mount and when account type changes
  useEffect(() => {
    loadAITradingData();
    
    // Only check service status once on mount, and only if we have active strategies
    // This prevents unnecessary health checks when backend service is not needed
    if (activeStrategies.length > 0 && !serviceCheckAttempted) {
      checkServiceStatus();
    }
  }, [loadAITradingData, checkServiceStatus, activeStrategies.length, serviceCheckAttempted]);

  // DISABLED: Auto-resume AI trading - This was causing AI to restart after manual stop
  // Auto-resume is now disabled to prevent unwanted restarts after manual stop
  useEffect(() => {
    // Only auto-resume on initial page load, not after manual stops
    const autoResumeAI = async () => {
      // Check if this is initial load (no manual stop has occurred)
      const hasManualStopFlag = localStorage.getItem('aiManualStop') === 'true';
      
      if (!hasManualStopFlag && isAIEnabled && serviceAvailable && !isServiceRunning && activeStrategies.length > 0) {
        // Check if any assets are actually enabled and active (not just strategy exists)
        const hasActiveEnabledAssets = aiTradingData?.assets && 
          Object.values(aiTradingData.assets).some((asset: any) => 
            asset.enabled && 
            asset.activeStrategyInstance?.isActive && 
            asset.activeStrategyInstance?.aiTradeStartEnabled
          );
        
        if (hasActiveEnabledAssets) {
          try {
            // console.log('🚀 Auto-resuming AI trading backend service (initial load only)...');
            await startBackendService();
          } catch (error) {
            console.error('❌ Failed to auto-resume AI trading:', error);
          }
        } else {
          // console.log('⏸️ Skip auto-resume: No active enabled assets found');
        }
      } else if (hasManualStopFlag) {
        // console.log('⏸️ Skip auto-resume: Manual stop detected, not auto-restarting');
      }
    };

    // Add a small delay to ensure all contexts are loaded
    const timer = setTimeout(autoResumeAI, 3000);
    return () => clearTimeout(timer);
  }, [isAIEnabled, serviceAvailable, isServiceRunning, activeStrategies.length, startBackendService, aiTradingData]);

  // Reload AI trading data when user document changes
  useEffect(() => {
    loadAITradingData();
  }, [loadAITradingData]);

  // 🔥 NEW: Listen for AI strategies restored on page reload
  useEffect(() => {
    const handleAIStrategiesRestored = (event: any) => {
      const { strategies, accountType: restoredAccountType } = event;
      
      // Only process if it's for the current account type
      if (restoredAccountType === accountType && strategies.length > 0) {
        console.log(`🔄 Backend AI: Restoring ${strategies.length} AI strategies on page reload`);
        
        // Auto-start the backend service if it's not running
        if (serviceAvailable && !isServiceRunning) {
          console.log(`🚀 Backend AI: Auto-starting service for restored strategies`);
          startBackendService();
        }
      }
    };

    positionEventEmitter.on('aiStrategiesRestored', handleAIStrategiesRestored);
    
    return () => {
      positionEventEmitter.off('aiStrategiesRestored', handleAIStrategiesRestored);
    };
  }, [accountType, serviceAvailable, isServiceRunning, startBackendService]);

  // ==================== NEW PER-ASSET AI TRADING METHODS ====================

  /**
   * Get AI trading configuration for a specific asset (memoized)
   */
  const getAssetAIConfig = useCallback((symbol: string) => {
    // Reduce logging to prevent infinite loops
    if (!aiTradingData || !aiTradingData.assets) {
      return null;
    }
    
    const result = aiTradingData.assets[symbol] || null;
    return result;
  }, [aiTradingData]);

  /**
   * Check if AI trading is enabled for a specific asset (memoized)
   */
  const isAssetAIEnabled = useCallback((symbol: string) => {
    if (!aiTradingData || !aiTradingData.assets) {
      return false;
    }
    const assetConfig = aiTradingData.assets[symbol];
    return assetConfig ? assetConfig.enabled : false;
  }, [aiTradingData]);

  /**
   * Enable or disable AI trading for a specific asset
   */
  const setAssetAIEnabled = useCallback(async (symbol: string, enabled: boolean) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
      
      // Clear manual stop flag when enabling AI (user wants to start AI)
      if (enabled) {
        localStorage.removeItem('aiManualStop');
        // console.log(`🚀 Manual stop flag cleared - AI can be started`);
      }
      
      await AIStrategyPersistenceService.setAssetAITradingEnabled(
        user.uid,
        accountType === 'live' ? 'live' : 'demo',
        symbol,
        enabled
      );
      
      // Immediate reload for both enable and disable operations
      await loadAITradingData();
      // console.log(`✅ AI Trading ${enabled ? 'enabled' : 'disabled'} for ${symbol}`);
    } catch (error) {
      console.error(`❌ Error setting AI enabled state for ${symbol}:`, error);
      throw error;
    }
  }, [user, accountType, loadAITradingData]);

  /**
   * COMPREHENSIVE STOP: Completely stop AI trading for an asset (sets ALL flags to false)
   * This method ensures ALL AI-related flags are immediately saved to Firestore
   */
  const stopAssetAITrading = useCallback(async (symbol: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
      
      // Set manual stop flag to prevent auto-restart
      localStorage.setItem('aiManualStop', 'true');
      // console.log(`🛑 Manual stop flag set to prevent auto-restart`);
      
      // Use the comprehensive stop method that sets ALL flags to false
      await AIStrategyPersistenceService.stopAssetAITrading(
        user.uid,
        accountType === 'live' ? 'live' : 'demo',
        symbol
      );
      
      // Immediate reload after comprehensive stop (no delays)
      await loadAITradingData();
      
      // console.log(`✅ COMPREHENSIVE STOP COMPLETE: All AI flags disabled for ${symbol}`);
    } catch (error) {
      console.error(`❌ Error in comprehensive stop for ${symbol}:`, error);
      throw error;
    }
  }, [user, accountType, loadAITradingData]);

  /**
   * COMPLETE REMOVAL: Remove strategy completely from activeStrategies array
   * This method completely removes the strategy from Firestore activeStrategies array
   */
  const removeStrategyCompletely = useCallback(async (symbol: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
      
      // Use the complete removal method that removes strategy from activeStrategies array
      await AIStrategyPersistenceService.removeStrategyCompletely(
        user.uid,
        accountType === 'live' ? 'live' : 'demo',
        symbol
      );
      
      // Immediate reload after complete removal
      await loadAITradingData();
      
      // console.log(`✅ COMPLETE REMOVAL COMPLETE: Strategy removed for ${symbol}`);
    } catch (error) {
      console.error(`❌ Error in complete removal for ${symbol}:`, error);
      throw error;
    }
  }, [user, accountType, loadAITradingData]);

  /**
   * Save AI strategy configuration for a specific asset (does NOT start AI trading)
   */
  const saveAssetStrategy = useCallback(async (symbol: string, strategy: any) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // console.log(`💾 saveAssetStrategy called for ${symbol}:`, {
      //   userId: user.uid,
      //   accountType: accountType === 'live' ? 'live' : 'demo',
      //   symbol,
      //   strategy: {
      //     id: strategy.id,
      //     name: strategy.name,
      //     type: strategy.type,
      //     isActive: strategy.isActive,
      //     aiTradeStartEnabled: strategy.aiTradeStartEnabled,
      //     hasConfig: !!strategy.config,
      //     configKeys: strategy.config ? Object.keys(strategy.config) : []
      //   }
      // });

      const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
      
      await AIStrategyPersistenceService.saveAssetStrategy(
        user.uid,
        accountType === 'live' ? 'live' : 'demo',
        symbol,
        strategy
      );
      
      // console.log(`✅ AIStrategyPersistenceService.saveAssetStrategy completed for ${symbol}`);
      
      // Reload AI trading data to reflect changes
      await loadAITradingData();
      
      // console.log(`✅ Strategy saved and data reloaded for ${symbol}`);
      
      // 🔥 CRITICAL DEBUG: Data reload completed - check PositionContext logs for verification
    } catch (error) {
      console.error(`❌ Error saving strategy for ${symbol}:`, error);
      throw error;
    }
  }, [user, accountType, loadAITradingData]);

  /**
   * Start AI trading for a specific asset (sets isActive to true)
   */
  const startAssetAITrading = useCallback(async (symbol: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      console.log(`🚀 startAssetAITrading called for ${symbol}`);

      const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
      
      await AIStrategyPersistenceService.setAssetAITradingEnabled(
        user.uid,
        accountType === 'live' ? 'live' : 'demo',
        symbol,
        true
      );
      
      // console.log(`✅ AIStrategyPersistenceService.setAssetAITradingEnabled completed for ${symbol}`);
      
      // Reload AI trading data to reflect changes
      await loadAITradingData();
      
      console.log(`✅ AI trading started and data reloaded for ${symbol}`);
      
    } catch (error) {
      console.error(`❌ Error starting AI trading for ${symbol}:`, error);
      throw error;
    }
  }, [user, accountType, loadAITradingData]);

  /**
   * Stop AI trading for a specific asset (sets isActive to false)
   */
  const stopAssetAITradingNew = useCallback(async (symbol: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      console.log(`🛑 stopAssetAITrading called for ${symbol}`);

      const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
      
      await AIStrategyPersistenceService.stopAssetAITrading(
        user.uid,
        accountType === 'live' ? 'live' : 'demo',
        symbol
      );
      
      // console.log(`✅ AIStrategyPersistenceService.stopAssetAITrading completed for ${symbol}`);
      
      // Reload AI trading data to reflect changes
      await loadAITradingData();
      
      console.log(`✅ AI trading stopped and data reloaded for ${symbol}`);
      
    } catch (error) {
      console.error(`❌ Error stopping AI trading for ${symbol}:`, error);
      throw error;
    }
  }, [user, accountType, loadAITradingData]);

  /**
   * Get available strategies for a specific asset
   */
  const getAssetStrategies = useCallback((symbol: string) => {
    const assetConfig = getAssetAIConfig(symbol);
    return assetConfig ? assetConfig.availableStrategies || [] : [];
  }, [getAssetAIConfig]);

  /**
   * Get list of active asset symbols
   */
  const getActiveAssets = useCallback(() => {
    if (!aiTradingData || !aiTradingData.globalPerformance) {
      return [];
    }
    return aiTradingData.globalPerformance.activeAssets || [];
  }, [aiTradingData]);

  /**
   * Get all active assets with their configurations
   */
  const getAllActiveAssets = useCallback(() => {
    if (!aiTradingData || !aiTradingData.assets) {
      return [];
    }
    
    const activeAssets: Array<{ symbol: string; config: any }> = [];
    
    for (const [symbol, config] of Object.entries(aiTradingData.assets)) {
      if (config.enabled) {
        activeAssets.push({ symbol, config });
      }
    }
    
    return activeAssets;
  }, [aiTradingData]);

  /**
   * Migration helper to fix assets with active strategies but enabled=false
   */
  const fixAssetEnabledStates = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      // console.log('🔧 Running CRITICAL database structure fix...');
      const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
      
      await AIStrategyPersistenceService.fixAssetEnabledStates(
        user.uid,
        accountType === 'live' ? 'live' : 'demo'
      );
      
      // Force reload AI trading data after fix
      // console.log('🔄 Reloading AI data after database fix...');
      await loadAITradingData();
      
      // Force backend to reload strategies immediately instead of waiting 5 minutes
      // console.log('🔄 Forcing backend service to reload strategies...');
      try {
        await backendAIService.reloadStrategies();
        //  console.log('✅ Backend strategies reloaded successfully');
      } catch (error) {
        console.error('⚠️ Backend reload failed, service will use next scheduled reload:', error);
      }
      
      // Force refresh service status to pick up changes  
      // console.log('🔄 Triggering backend service refresh...');
      await checkServiceStatus();
      
      // console.log('✅ CRITICAL database fix and refresh completed');
      
    } catch (error) {
      console.error('❌ Error in CRITICAL database structure fix:', error);
    }
  }, [user, accountType, loadAITradingData, checkServiceStatus]);

  // Run migration fix on startup to fix any assets with enabled=false but active strategies
  // BUT only run once on initial load, not after every state change to prevent re-enabling stopped assets
  useEffect(() => {
    const runMigrationFix = async () => {
      if (user && aiTradingData) {
        // Only run migration fix if we detect assets that genuinely need fixing
        // (enabled=false but isActive=true AND aiTradeStartEnabled=true)
        // This prevents re-enabling assets that were manually stopped (they have isActive=false)
        let needsFix = false;
        if (aiTradingData.assets) {
          Object.values(aiTradingData.assets).forEach((assetConfig: any) => {
            if (!assetConfig.enabled && 
                assetConfig.activeStrategyInstance &&
                assetConfig.activeStrategyInstance.isActive === true &&
                assetConfig.activeStrategyInstance.aiTradeStartEnabled === true) {
              // console.log('🔧 Found asset needing migration fix:', {
              //   enabled: assetConfig.enabled,
              //   isActive: assetConfig.activeStrategyInstance.isActive,
              //   aiTradeStartEnabled: assetConfig.activeStrategyInstance.aiTradeStartEnabled
              // });
              needsFix = true;
            }
          });
        }
        
        if (needsFix) {
          await fixAssetEnabledStates();
        } else {
          
        }
      }
    };

    // Only run migration fix on initial load, not on every state change
    const timer = setTimeout(runMigrationFix, 2000); // Delay to avoid conflicts with manual stops
    return () => clearTimeout(timer);
  }, [user, fixAssetEnabledStates]); // Removed aiTradingData dependency to prevent repeated runs

  const contextValue: BackendAITradingContextType = {
    // Service status
    isServiceRunning,
    serviceAvailable,
    activeStrategiesCount,
    
    // AI Trading data
    aiTradingData,
    isAIEnabled,
    strategies,
    activeStrategies,
    
    // Per-asset AI trading functionality
    getAssetAIConfig,
    setAssetAIEnabled,
    stopAssetAITrading, // NEW: Comprehensive stop method
    removeStrategyCompletely, // NEW: Complete removal method
    isAssetAIEnabled,
    saveAssetStrategy,
    startAssetAITrading, // NEW: Start AI trading
    stopAssetAITradingNew, // NEW: Stop AI trading
    getAssetStrategies,
    getActiveAssets,
    getAllActiveAssets,
    
    // Data refresh functionality
    loadAITradingData, // NEW: Force reload AI data from Firestore
    
    // Migration and maintenance
    fixAssetEnabledStates,
    
    // Global actions (backward compatibility)
    enableAI,
    disableAI,
    addStrategy,
    updateStrategy,
    removeStrategy,
    activateStrategy,
    deactivateStrategy,
    
    // Service control
    startBackendService,
    stopBackendService,
    
    // Status
    isLoading,
    error: error || null
  };

  return (
    <BackendAITradingContext.Provider value={contextValue}>
      {children}
    </BackendAITradingContext.Provider>
  );
};
